import { Cancel01Icon as X, KeyboardIcon as Keyboard, Mouse01Icon as Mouse, Idea01Icon as Lightbulb } from 'hugeicons-react'
import { motion, AnimatePresence } from 'framer-motion'

interface HelpModalProps {
    isOpen: boolean
    onClose: () => void
}

export default function HelpModal({ isOpen, onClose }: HelpModalProps) {
    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-surface-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-white/10">
                        <div>
                            <h2 className="text-xl font-semibold text-white">Editor Shortcuts & Tips</h2>
                            <p className="text-sm text-txt-secondary mt-1">Get the most out of your editing workflow</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-txt-dim hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">

                        {/* Keyboard Shortcuts */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-brand-400 mb-2">
                                <Keyboard size={18} />
                                <h3 className="font-medium text-white">Keyboard Shortcuts</h3>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-txt-secondary">Play / Pause</span>
                                    <kbd className="px-2 py-1 bg-surface-800 rounded border border-white/10 font-mono text-xs text-white">Space</kbd>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-txt-secondary">Split Clip</span>
                                    <kbd className="px-2 py-1 bg-surface-800 rounded border border-white/10 font-mono text-xs text-white">S</kbd>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-txt-secondary">Delete Selected</span>
                                    <kbd className="px-2 py-1 bg-surface-800 rounded border border-white/10 font-mono text-xs text-white">Del</kbd>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-txt-secondary">Undo</span>
                                    <kbd className="px-2 py-1 bg-surface-800 rounded border border-white/10 font-mono text-xs text-white">Ctrl + Z</kbd>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-txt-secondary">Redo</span>
                                    <kbd className="px-2 py-1 bg-surface-800 rounded border border-white/10 font-mono text-xs text-white">Ctrl + Y</kbd>
                                </div>
                            </div>
                        </div>

                        {/* Interactions & Tips */}
                        <div className="space-y-6">
                            {/* Mouse Info */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-brand-400 mb-2">
                                    <Mouse size={18} />
                                    <h3 className="font-medium text-white">Timeline Interactions</h3>
                                </div>
                                <ul className="text-xs text-txt-secondary space-y-2 list-disc list-inside">
                                    <li><span className="text-white">Click & Drag</span> on timeline to scrub video.</li>
                                    <li><span className="text-white">Drag Edges</span> of Zoom Regions to resize them.</li>
                                    <li><span className="text-white">Click</span> on Zoom Track to add a keyframe.</li>
                                </ul>
                            </div>

                            {/* Tips */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-warning-400 mb-2">
                                    <Lightbulb size={18} />
                                    <h3 className="font-medium text-white">Pro Tips</h3>
                                </div>
                                <p className="text-xs text-txt-secondary leading-relaxed">
                                    Use <span className="text-white font-medium">Auto-Generate Zoom</span> to instantly create smooth zooms based on your mouse movement recording. You can then fine-tune them on the timeline.
                                </p>
                                <p className="text-xs text-txt-secondary leading-relaxed">
                                    Try different <span className="text-white font-medium">3D Point of Views</span> in the Layout tab to give your screen recordings a professional studio look.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 bg-surface-950/50 border-t border-white/5 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors"
                        >
                            Got it
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
